import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BILLING_METHOD_LABELS, EXTRA_USER_GROSS_MONTH_CENTS, formatCents, INCLUDED_USERS, MODULES, modulePlan, PLAN_LABELS, PLANS, TRIAL_PLAN, type BillingMethod, type BillingOverview, type PlaceOrderResult, type Plan } from '@kontor/shared';
import { api, ApiError } from '../api';
import { WEBSITE } from '../links';
import { useMeta } from '../meta';
import './billing.css';

/** Was im Paket steckt, nennt die Zuordnung des Betreibers: die Module, die erst mit diesem Paket dazukommen */
function planText(plan: Plan): string {
  const own = MODULES.filter((m) => modulePlan(m) === plan && m.available && !['users', 'setup'].includes(m.key)).map((m) => m.label);
  const base = PLANS.indexOf(plan) > 0 ? `Alles aus ${PLAN_LABELS[PLANS[PLANS.indexOf(plan) - 1]!]}, zusätzlich ` : '';
  return `${base}${own.join(', ')}.`;
}
const METHOD_TEXT: Record<BillingMethod, string> = {
  invoice: 'Sie bekommen für jeden Abrechnungszeitraum von zwölf Monaten eine Rechnung per E-Mail und überweisen sie innerhalb von 14 Tagen.',
  stripe: 'Sie geben Ihre Zahlungsdaten bei unserem Zahlungsdienstleister Stripe ein. Der Betrag wird einmal im Jahr eingezogen.',
  paypal: 'Sie bestätigen das Abonnement bei PayPal. Der Betrag wird einmal im Jahr eingezogen.',
  bank: 'Sie zahlen mit Ihrem Online-Banking. Diese Zahlungsart gibt es nur bei der Bestellung auf der Webseite.',
};

const errorText = (e: unknown) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Die Bestellung hat nicht geklappt.');

/**
 * Bestellung eines Pakets in der Testphase (KON-180). Mit der Bestellung wird aus dem Test ein bezahlter Zugang, die Grenzen des Tests entfallen und alle Daten bleiben.
 * Die Zahlungsdaten bei Karte, Lastschrift und PayPal gibt der Kunde beim Zahlungsanbieter ein, sie erreichen Wirkia nie.
 */
export function OrderPage() {
  useMeta();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const overview = useQuery({ queryKey: ['billing'], queryFn: async () => (await api<{ data: BillingOverview }>('GET', '/billing')).data });
  // Aus dem Wirkia Konto kommt der Paketwunsch als ?paket=…
  const wanted = params.get('paket');
  const [plan, setPlan] = useState<Plan>((PLANS as readonly string[]).includes(wanted ?? '') ? (wanted as Plan) : 'standard');
  const [method, setMethod] = useState<BillingMethod>('invoice');
  const [billing, setBilling] = useState({ company: '', name: '', street: '', postalCode: '', city: '', vatId: '', email: '' });
  const [terms, setTerms] = useState(false);
  const [avv, setAvv] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const place = useMutation({
    mutationFn: async () =>
      (await api<{ data: PlaceOrderResult }>('POST', '/billing/orders', { plan, paymentMethod: method, billing: { ...billing, country: 'DE', vatId: billing.vatId || undefined }, acceptTerms: terms, acceptAvv: avv })).data,
    onSuccess: (result) => {
      client.invalidateQueries();
      if (result.redirectUrl) window.location.href = result.redirectUrl;
      else navigate('/einrichtung/wirkia-konto?bestellt=1');
    },
    onError: (e) => setError(errorText(e)),
  });

  if (overview.isLoading) return <p className="muted">Wird geladen …</p>;
  const data = overview.data;
  if (!data) return <div className="alert alert--error" role="alert">Die Pakete konnten nicht geladen werden.</div>;
  if (!data.canOrder) {
    return (
      <div className="card narrow">
        <h1>Paket bestellen</h1>
        <p>{data.order ? 'Für Ihren Zugang gibt es schon eine Bestellung.' : 'Bestellen können Sie in der Testphase.'}</p>
        <p><Link to="/einrichtung/wirkia-konto" className="btn btn--primary">Zum Wirkia Konto</Link></p>
      </div>
    );
  }
  const offer = data.plans.find((p) => p.plan === plan)!;
  const set = (key: keyof typeof billing) => (e: { target: { value: string } }) => setBilling((b) => ({ ...b, [key]: e.target.value }));

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    place.mutate();
  }

  return (
    <>
      <div className="page-head">
        <h1>Paket bestellen</h1>
      </div>
      <p className="muted">Mit der Bestellung entfallen die Grenzen des Tests (begrenzter Speicher und Fotos), und alle Ihre Daten bleiben erhalten. Das Paket wird für zwölf Monate im Voraus abgerechnet und lässt sich jederzeit zum Ende des Abrechnungszeitraums kündigen.</p>
      {params.get('abgebrochen') && <div className="alert alert--warn" role="status">Die Zahlung wurde abgebrochen. Ihre Bestellung ist nicht zustande gekommen, Sie können es noch einmal versuchen.</div>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <form onSubmit={submit} noValidate>
        <h2>Paket</h2>
        <div className="plan-grid" role="radiogroup" aria-label="Paket">
          {data.plans.map((p) => (
            <label key={p.plan} className={`plan-card card${plan === p.plan ? ' plan-card--on' : ''}`}>
              <input type="radio" name="plan" value={p.plan} checked={plan === p.plan} onChange={() => setPlan(p.plan)} />
              <strong>{PLAN_LABELS[p.plan]}</strong>
              <span className="plan-card__price">{formatCents(p.grossMonthCents)} <small>brutto je Benutzer und Monat</small></span>
              <span className="muted small">{formatCents(p.grossCents)} für zwölf Monate im Voraus</span>
              <span className="small">{planText(p.plan)}</span>
            </label>
          ))}
        </div>
        <p className="muted small">Alle Preise sind Bruttopreise inklusive Umsatzsteuer, je Benutzer und Monat. In jedem Paket ist {INCLUDED_USERS} Benutzer enthalten, jeder weitere kostet {formatCents(EXTRA_USER_GROSS_MONTH_CENTS)} im Monat und lässt sich später unter „Benutzer“ buchen. Ein günstigeres Paket als {PLAN_LABELS[TRIAL_PLAN]} schaltet Module ab, die Sie im Test genutzt haben. Ihre Daten bleiben dabei gespeichert. Die erste Einrichtung und Unterstützung durch Wirkia erfolgt nach individuellem Angebot, schreiben Sie uns an kontakt@wirkia.de.</p>

        <h2>Rechnungsanschrift</h2>
        <div className="card">
          <div className="form-grid">
            <label className="field"><span>Firma <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.company} onChange={set('company')} autoComplete="organization" required /></label>
            <label className="field"><span>Ansprechpartner <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.name} onChange={set('name')} autoComplete="name" required /></label>
            <label className="field"><span>Straße und Hausnummer <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.street} onChange={set('street')} autoComplete="street-address" required /></label>
            <label className="field"><span>Postleitzahl <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.postalCode} onChange={set('postalCode')} autoComplete="postal-code" required /></label>
            <label className="field"><span>Ort <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.city} onChange={set('city')} autoComplete="address-level2" required /></label>
            <label className="field"><span>Umsatzsteuer-ID (freiwillig)</span><input className="input" value={billing.vatId} onChange={set('vatId')} placeholder="DE123456789" /></label>
            <label className="field field--wide"><span>E-Mail-Adresse für die Rechnung <abbr title="Pflichtfeld">*</abbr></span><input className="input" type="email" value={billing.email} onChange={set('email')} autoComplete="email" required /></label>
          </div>
        </div>

        <h2>Zahlungsart</h2>
        <div className="card">
          {data.methods.map((m) => (
            <label key={m.key} className={`check${m.enabled ? '' : ' check--off'}`}>
              <input type="radio" name="method" value={m.key} checked={method === m.key} disabled={!m.enabled} onChange={() => setMethod(m.key)} />
              <span>
                <strong>{BILLING_METHOD_LABELS[m.key]}</strong>
                <span className="muted small billing-block">{m.enabled ? METHOD_TEXT[m.key] : 'Diese Zahlungsart ist noch nicht verfügbar.'}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="card billing-summary">
          <p>
            <strong>{PLAN_LABELS[plan]}</strong>: {formatCents(offer.grossMonthCents)} brutto je Benutzer und Monat. Die erste Abrechnung gilt ab heute für zwölf Monate: {formatCents(offer.grossCents)} brutto.
          </p>
          <label className="check">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
            <span>Ich habe die <a href={`${WEBSITE}/agb/`} target="_blank" rel="noreferrer">Allgemeinen Geschäftsbedingungen</a> gelesen und akzeptiere sie. <abbr title="Pflichtfeld">*</abbr></span>
          </label>
          <label className="check">
            <input type="checkbox" checked={avv} onChange={(e) => setAvv(e.target.checked)} />
            <span>Ich schließe den <a href={`${WEBSITE}/avv/`} target="_blank" rel="noreferrer">Vertrag zur Auftragsverarbeitung</a> ab. <abbr title="Pflichtfeld">*</abbr></span>
          </label>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={place.isPending}>{place.isPending ? 'Wird bestellt …' : method === 'invoice' ? 'Zahlungspflichtig bestellen' : 'Weiter zur Zahlung'}</button>
            <Link to="/" className="btn btn--secondary">Abbrechen</Link>
          </div>
        </div>
      </form>
    </>
  );
}
